/**
 * The one way to answer a tool request (ADR 0072), on every surface: the Inbox panel, the session
 * drawer, Overview's Needs you, mission pages, the bell and the phone. Same four actions with the
 * same names (decision.ts), the same inline forms (a deny note, the session pattern, the rule
 * pattern, each visible and editable), and the same high-risk confirm.
 *
 *   const c = useDecision(a, { machine });     // state, the confirm dialog, the keys
 *   <ApprovalAnswer c={c} layout="bar" keys /> // the buttons, or the open form
 *   {c.dialog}
 *
 * An answer cannot be taken back once sent; a session allow and a rule can, from the toast.
 */
import { Ban, Check, CheckCheck, Ellipsis, ScrollText, Undo2 } from '@orgus/ui/icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { ApprovalRequest } from '@orgus/protocol';
import {
  Button,
  ConfirmDialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  IconButton,
  Input,
  RadioGroup,
  RadioGroupItem,
  Textarea,
  cn,
  toUiError,
  toast,
  undoable,
} from '@orgus/ui';
import { DraftFiles, useDraftFiles } from '../attachments/DraftFiles.tsx';
import { revokeSessionAllow } from '../autonomy/api.ts';
import { commandOf } from './blocked.ts';
import { ACTION_DONE, ACTION_KEY, ACTION_LABEL, decisionOf, needsConfirm, patternHint, unavailable } from './decision.ts';
import type { DecisionAction, DecisionInput } from './decision.ts';
import { shortTitle } from './inboxModel.ts';
import { decideApproval, deleteRule } from './live.ts';

/** Which inline form is open: a deny note, the session pattern, or the rule pattern. */
export type AnswerForm = 'deny' | 'session' | 'rule' | null;

const FORM_OF: Partial<Record<DecisionAction, AnswerForm>> = { deny: 'deny', 'allow-session': 'session', 'make-rule': 'rule' };

export interface DecisionOptions {
  /** The machine's name, for the high-risk confirm ("It runs on build-02"). */
  machine?: string;
  /** The project's name, for the rule form's label. */
  project?: string;
  /** A deny note can carry files (the Inbox and the phone). */
  files?: boolean;
  /** After an answer went through. */
  onDone?: (action: DecisionAction) => void;
  /** How the answer is sent (default sendDecision): the phone keeps a once or a deny for when it is back online. */
  send?: (a: ApprovalRequest, action: DecisionAction, input: DecisionInput, attachmentIds: string[]) => Promise<void>;
}

export interface Decision {
  a: ApprovalRequest;
  opts: DecisionOptions;
  busy: DecisionAction | null;
  error: string | null;
  form: AnswerForm;
  setForm: (f: AnswerForm) => void;
  /** Answer now, or open the high-risk confirm first. Resolves true once the answer went through. */
  run: (action: DecisionAction, input?: DecisionInput) => Promise<boolean>;
  /** Start an action the way its button does: a form for deny, session and rule; Allow once at once (or its confirm). */
  start: (action: DecisionAction) => void;
  /**
   * The Inbox's keys for the request that is visibly open: a, s, r, d, and Esc to close a form.
   * Returns true when it took the key.
   */
  onKey: (e: KeyboardEvent) => boolean;
  files: ReturnType<typeof useDraftFiles>;
  /** The high-risk confirm; render it once. */
  dialog: ReactNode;
}

/** Send one answer, show what happened, and offer to take back what outlives it. */
export async function sendDecision(a: ApprovalRequest, action: DecisionAction, input: DecisionInput = {}, attachmentIds: string[] = []): Promise<void> {
  const d = decisionOf(action, input);
  const res = await decideApproval(a.id, attachmentIds.length && action === 'deny' ? { ...d, attachmentIds } : d);
  const what = shortTitle(a);
  if (action === 'allow-session') {
    const pattern = input.pathScope === 'worktree' ? `${a.worktree}/*` : d.pattern ?? '';
    undoable({
      title: ACTION_DONE[action],
      description: what,
      undoneTitle: 'Allow taken back',
      undo: () => revokeSessionAllow({ sessionId: a.sessionId, machineId: a.machineId, tool: a.tool ?? '', pattern, by: res.decidedBy ?? '', at: res.decidedAt ?? Date.now() }),
    });
  } else if (action === 'make-rule' && res.ruleId) {
    const ruleId = res.ruleId;
    undoable({ title: ACTION_DONE[action], description: what, undoneTitle: 'Rule removed', undo: () => deleteRule(ruleId) });
  } else {
    toast(ACTION_DONE[action], { tone: action === 'deny' ? 'neutral' : 'success', description: what });
  }
}

export function useDecision(a: ApprovalRequest, opts: DecisionOptions = {}): Decision {
  const [busy, setBusy] = useState<DecisionAction | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setFormState] = useState<AnswerForm>(null);
  const [confirm, setConfirm] = useState<{ action: DecisionAction; input: DecisionInput } | null>(null);
  const files = useDraftFiles();
  const live = useRef(true);
  useEffect(() => () => void (live.current = false), []);

  const setForm = useCallback((f: AnswerForm) => {
    setError(null);
    setFormState(f);
  }, []);

  const send = useCallback(
    async (action: DecisionAction, input: DecisionInput): Promise<void> => {
      await (opts.send ?? sendDecision)(a, action, input, opts.files ? files.ids : []);
      if (action === 'deny') files.clear();
      if (live.current) setFormState(null);
      opts.onDone?.(action);
    },
    [a, opts, files],
  );

  const run = useCallback(
    async (action: DecisionAction, input: DecisionInput = {}): Promise<boolean> => {
      if (busy) return false;
      const why = unavailable(a, action);
      if (why) {
        setError(why);
        return false;
      }
      if (needsConfirm(a, action)) {
        setConfirm({ action, input });
        return false;
      }
      setBusy(action);
      setError(null);
      try {
        await send(action, input);
        return true;
      } catch (e) {
        const err = toUiError(e);
        if (live.current) setError([err.what, err.why, err.next].filter(Boolean).join(' '));
        return false;
      } finally {
        if (live.current) setBusy(null);
      }
    },
    [a, busy, send],
  );

  const start = useCallback(
    (action: DecisionAction) => {
      const f = FORM_OF[action];
      if (f) setForm(f);
      else void run(action);
    },
    [run, setForm],
  );

  const onKey = useCallback(
    (e: KeyboardEvent): boolean => {
      if (busy || confirm) return false;
      if (e.key === 'Escape' && form) {
        setForm(null);
        return true;
      }
      if (form) return false;
      const action = (Object.keys(ACTION_KEY) as DecisionAction[]).find((k) => ACTION_KEY[k] === e.key);
      if (!action) return false;
      start(action);
      return true;
    },
    [busy, confirm, form, setForm, start],
  );

  const preview = commandOf(a) ?? a.title;
  const dialog = (
    <ConfirmDialog
      open={!!confirm}
      onOpenChange={(o) => !o && setConfirm(null)}
      title={`Allow "${shortTitle(a, 40)}"?`}
      description={`High risk: it runs on ${opts.machine ?? 'its machine'} as soon as you allow it.`}
      confirmLabel={confirm ? ACTION_LABEL[confirm.action] : ACTION_LABEL['allow-once']}
      cancelLabel="Keep waiting"
      onConfirm={async () => {
        if (!confirm) return;
        setBusy(confirm.action);
        try {
          await send(confirm.action, confirm.input);
        } finally {
          if (live.current) setBusy(null);
        }
      }}
    >
      <pre className="max-h-32 overflow-auto whitespace-pre-wrap break-all rounded-control bg-sunken px-3 py-2 font-mono text-caption text-fg-muted">{preview}</pre>
    </ConfirmDialog>
  );

  return { a, opts, busy, error, form, setForm, run, start, onKey, files, dialog };
}

// ---------------------------------------------------------------------------
// The buttons and forms
// ---------------------------------------------------------------------------

export interface ApprovalAnswerProps {
  c: Decision;
  /**
   * 'bar': a panel or drawer footer (More, Allow for session, Deny, Allow once).
   * 'compact': a card, popover or row (More, Deny, Allow once).
   * 'phone': 44 px buttons, Deny and Allow once side by side, More under them.
   */
  layout?: 'bar' | 'compact' | 'phone';
  /** Show each button's key (only where the keys work: the Inbox's open request). */
  keys?: boolean;
  /** More items for the More menu (Open session, Open in Inbox). */
  menu?: ReactNode;
  className?: string;
}

export function ApprovalAnswer({ c, layout = 'compact', keys = false, menu, className }: ApprovalAnswerProps) {
  const phone = layout === 'phone';
  const size = phone ? 'lg' : 'sm';
  const btn = phone ? 'h-11' : undefined;
  const k = (action: DecisionAction) => (keys ? [ACTION_KEY[action]] : undefined);
  const { a, busy, form } = c;

  if (form) return <AnswerFormView c={c} phone={phone} className={className} />;

  const more = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {phone ? (
          <Button size="lg" variant="ghost" className="h-11" icon={<Ellipsis />} disabled={!!busy}>
            More answers
          </Button>
        ) : (
          <IconButton size="sm" label="More answers" icon={<Ellipsis />} disabled={!!busy} />
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align={phone ? 'center' : 'start'} className="w-56">
        {layout !== 'bar' && (
          <DropdownMenuItem icon={<CheckCheck />} shortcut={k('allow-session')} disabled={!!unavailable(a, 'allow-session')} onSelect={() => c.start('allow-session')}>
            {ACTION_LABEL['allow-session']}…
          </DropdownMenuItem>
        )}
        <DropdownMenuItem icon={<ScrollText />} shortcut={k('make-rule')} disabled={!!unavailable(a, 'make-rule')} onSelect={() => c.start('make-rule')}>
          {ACTION_LABEL['make-rule']}…
        </DropdownMenuItem>
        {menu}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className={cn('flex w-full flex-col gap-2', className)}>
      {c.error && <ErrorLine text={c.error} />}
      {phone ? (
        <>
          <div className="grid grid-cols-2 gap-2">
            <Button size={size} variant="secondary" className={btn} icon={<Ban />} disabled={!!busy} onClick={() => c.start('deny')}>
              {ACTION_LABEL.deny}
            </Button>
            <Button size={size} variant={a.risk === 'high' ? 'danger' : 'primary'} className={btn} icon={<Check />} loading={busy === 'allow-once'} disabled={!!busy} onClick={() => c.start('allow-once')}>
              {ACTION_LABEL['allow-once']}
            </Button>
          </div>
          {more}
        </>
      ) : (
        <div className="flex w-full flex-wrap items-center justify-end gap-2">
          {more}
          <span className="mr-auto" />
          {layout === 'bar' && (
            <Button size={size} variant="ghost" icon={<CheckCheck />} shortcut={k('allow-session')} className="max-sm:hidden" disabled={!!busy} onClick={() => c.start('allow-session')}>
              {ACTION_LABEL['allow-session']}
            </Button>
          )}
          <Button size={size} variant="secondary" icon={<Ban />} shortcut={k('deny')} disabled={!!busy} onClick={() => c.start('deny')}>
            {ACTION_LABEL.deny}
          </Button>
          <Button size={size} variant="primary" icon={<Check />} shortcut={k('allow-once')} loading={busy === 'allow-once'} disabled={!!busy} onClick={() => c.start('allow-once')}>
            {ACTION_LABEL['allow-once']}
          </Button>
        </div>
      )}
    </div>
  );
}

function ErrorLine({ text }: { text: string }) {
  return (
    <p role="alert" className="rounded-control bg-status-danger-soft px-3 py-2 text-ui text-fg">
      {text}
    </p>
  );
}

/** The open form: a deny note (and files), or the pattern a session allow or a rule matches. */
function AnswerFormView({ c, phone, className }: { c: Decision; phone: boolean; className?: string }) {
  const { a, form, busy } = c;
  const [note, setNote] = useState('');
  const [pattern, setPattern] = useState(a.suggestedPattern ?? '');
  const [pathScope, setPathScope] = useState<'folder' | 'worktree'>('folder');
  const field = useRef<HTMLTextAreaElement & HTMLInputElement>(null);
  useEffect(() => {
    if (form === 'deny') field.current?.focus();
    else field.current?.select();
  }, [form]);
  const action: DecisionAction = form === 'deny' ? 'deny' : form === 'session' ? 'allow-session' : 'make-rule';
  const submit = () => void c.run(action, form === 'deny' ? { note } : { pattern, ...(form === 'session' && a.worktree ? { pathScope } : {}) });
  const close = () => c.setForm(null);
  const onEsc = (e: { key: string; stopPropagation: () => void }) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      close();
    }
  };
  const size = phone ? 'lg' : 'sm';
  return (
    <form
      className={cn('flex w-full flex-col gap-3', className)}
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      {form === 'deny' ? (
        <>
          <Textarea
            ref={field}
            label="Note for the agent"
            rows={2}
            value={note}
            placeholder="Optional"
            maxLength={1000}
            disabled={!!busy}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => {
              onEsc(e);
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
          />
          {c.opts.files && <DraftFiles drafts={c.files} disabled={!!busy} hint="Files go to the session's machine with your answer." />}
        </>
      ) : (
        <>
        {form === 'session' && a.worktree && (
          <div className="grid gap-2">
            <span className="text-ui text-fg-muted">Path scope</span>
            <RadioGroup aria-label="Path scope" value={pathScope} disabled={!!busy} onValueChange={(v) => setPathScope(v === 'worktree' ? 'worktree' : 'folder')}>
              <label className="flex items-center gap-2 text-ui"><RadioGroupItem value="folder" />Asking file's folder</label>
              <label className="flex items-center gap-2 text-ui"><RadioGroupItem value="worktree" />Session's whole worktree</label>
            </RadioGroup>
          </div>
        )}
        <Input
          ref={field}
          label={form === 'session' ? 'Allow for this session when it matches' : `Allow in ${c.opts.project ?? 'this project'} when it matches`}
          value={form === 'session' && pathScope === 'worktree' ? `${a.worktree}/*` : pattern}
          spellCheck={false}
          className="font-mono"
          disabled={!!busy || (form === 'session' && pathScope === 'worktree')}
          hint={form === 'rule' ? `${patternHint(pattern, a.tool)} Every session in the project, from now on.` : patternHint(pattern, a.tool)}
          onChange={(e) => setPattern(e.target.value)}
          onKeyDown={onEsc}
        />
        </>
      )}
      {c.error && <ErrorLine text={c.error} />}
      <div className={cn('flex gap-2', phone ? 'grid grid-cols-2' : 'justify-end')}>
        <Button size={size} variant="ghost" className={phone ? 'h-11' : undefined} icon={<Undo2 />} disabled={!!busy} onClick={close}>
          Cancel
        </Button>
        <Button
          size={size}
          type="submit"
          variant={form === 'deny' ? 'secondary' : 'primary'}
          className={phone ? 'h-11' : undefined}
          icon={form === 'deny' ? <Ban /> : form === 'session' ? <CheckCheck /> : <ScrollText />}
          loading={!!busy}
        >
          {form === 'deny' ? 'Deny request' : ACTION_LABEL[action]}
        </Button>
      </div>
    </form>
  );
}
